<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { fade } from 'svelte/transition';
  import type { heroItem } from '$src/js/components/organisms/homepage-hero/homepage-hero';

  let activeIndex = 0;
  let interval: ReturnType<typeof setInterval> | null = null;
  let videoTimeout: ReturnType<typeof setTimeout> | null = null;

  const INTERVAL_DURATION = 10000;
  const TRANSITION_DURATION = 500;
  const VIDEO_MAX_DURATION = 20000;

  const startInterval = () => {
    stopInterval();

    interval = setInterval(() => {
      activeIndex = (activeIndex + 1) % data.length;
    }, INTERVAL_DURATION);
  };

  const stopInterval = () => {
    if (interval) {
      clearInterval(interval);
      interval = null;
    }
  };

  const clearVideoTimeout = () => {
    if (videoTimeout) {
      clearTimeout(videoTimeout);
      videoTimeout = null;
    }
  };

  const goToSlide = (index: number) => {
    activeIndex = index;

    if (data[index].media.type === 'video') {
      stopInterval();
    } else {
      startInterval();
    }
  };

  const handleVideoLoad = () => {
    stopInterval();
    clearVideoTimeout();

    videoTimeout = setTimeout(() => {
      handleVideoEnded();
    }, VIDEO_MAX_DURATION);
  };

  const handleVideoEnded = () => {
    stopInterval();
    clearVideoTimeout();

    activeIndex = (activeIndex + 1) % data.length;

    if (data[activeIndex].media.type !== 'video') {
      startInterval();
    }
  };

  const handleVideoError = () => {
    handleVideoEnded();
  };

  onMount(() => {
    startInterval();
  });

  onDestroy(() => {
    stopInterval();
    clearVideoTimeout();
  });

  const data: heroItem[] = window.heroItems ?? [];
</script>

<div class="relative aspect-[2/1] max-h-[1080px] min-h-[560px] w-full sm:min-h-[640px]">
  <div class="absolute top-0 left-0 h-full w-full">
    {#key activeIndex}
      <div
        in:fade={{ duration: TRANSITION_DURATION, delay: TRANSITION_DURATION }}
        out:fade={{ duration: TRANSITION_DURATION }}
      >
        {#if data[activeIndex].media.type === 'image'}
          <img
            src={data[activeIndex].media.url}
            alt={data[activeIndex].title}
            class="absolute inset-0 h-full w-full object-cover"
          />
        {:else if data[activeIndex].media.type === 'video'}
          <video
            class="absolute inset-0 h-full w-full object-cover"
            autoplay
            muted
            playsinline
            onloadedmetadata={handleVideoLoad}
            onended={handleVideoEnded}
            onerror={handleVideoError}
          >
            <source src={data[activeIndex].media.url} type="video/mp4" />
          </video>
        {/if}
      </div>
    {/key}
  </div>

  <div class="bg-surface-overlay-primary absolute top-0 left-0 z-1 flex h-full w-full items-end">
    <div class="wrapper-inner">
      <div class="flex flex-col gap-14 lg:gap-30">
        <div class="grid *:col-start-1 *:row-start-1 md:flex-1">
          {#key activeIndex}
            <div class="flex max-w-[800px] flex-col items-start justify-end gap-4 md:gap-8">
              <h1
                class="font-heading text-40asrem sm:text-64asrem md:text-72asrem leading-[90%] font-bold text-balance wrap-break-word uppercase"
                in:fade={{ duration: TRANSITION_DURATION, delay: TRANSITION_DURATION }}
                out:fade={{ duration: TRANSITION_DURATION }}
              >
                {data[activeIndex].title}
              </h1>

              <p
                class="text-14asrem md:text-18asrem w-full max-w-[384px] leading-[150%] sm:text-base"
                in:fade={{ duration: TRANSITION_DURATION, delay: TRANSITION_DURATION }}
                out:fade={{ duration: TRANSITION_DURATION }}
              >
                {data[activeIndex].text}
              </p>

              <div
                in:fade={{ duration: TRANSITION_DURATION, delay: TRANSITION_DURATION }}
                out:fade={{ duration: TRANSITION_DURATION }}
              >
                {#if data[activeIndex].link}
                  <a
                    class="btn-link"
                    href={data[activeIndex].link.url}
                    target={data[activeIndex].link.target}
                  >
                    {data[activeIndex].link.label}
                  </a>
                {/if}
              </div>
            </div>
          {/key}
        </div>

        <div
          class="after:bg-black-300 relative flex after:absolute after:top-0 after:right-[-16px] after:h-full after:w-screen after:backdrop-blur-[4px] md:after:right-0"
        >
          <div class="relative z-1 flex w-full flex-row items-end">
            {#each data as item, i}
              <button
                onclick={() => goToSlide(i)}
                class={`group/hero-item hover:bg-surface-neutral-def-sunken relative flex h-18 items-center transition-all duration-300 sm:h-20 md:w-full ${activeIndex === i ? 'bg-surface-neutral-def-sunken after:bg-gold-1000 after:absolute after:top-[-4px] after:right-0 after:h-1 after:w-full after:content-[""]' : ''}`}
              >
                <div
                  class="after:bg-surface-overlay-secondary relative m-2 shrink-0 after:absolute after:inset-0 after:opacity-100 after:transition-all after:duration-300 {activeIndex ===
                  i
                    ? 'after:opacity-0!'
                    : 'group-hover/hero-item:after:opacity-0'}"
                >
                  <img
                    class="h-14 w-14 object-cover sm:h-16 sm:w-16"
                    src={item.thumbnail}
                    alt={item.title}
                  />
                </div>

                <span
                  class="font-sm line-clamp-2 h-10 px-4 text-left leading-[130%] font-bold max-md:hidden"
                >
                  {item.title}
                </span>
              </button>
            {/each}
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
